/**
 * Polyfill for React Router v5 hooks in v6 environment
 * This allows @aureskonnect/react-ui (which uses React Router v5) to work with React Router v6
 */

import {
    useNavigate,
    useLocation as useLocationV6,
    useParams as useParamsV6,
} from "react-router-dom";
import { useMemo } from "react";

/**
 * Polyfill for useHistory hook from React Router v5
 * Maps v6's useNavigate to v5's useHistory API
 */
export function useHistory() {
    const navigate = useNavigate();
    const location = useLocationV6();

    return useMemo(
        () => ({
            push: (path: string, state?: any) => navigate(path, { state }),
            replace: (path: string, state?: any) =>
                navigate(path, { replace: true, state }),
            go: (n: number) => navigate(n),
            goBack: () => navigate(-1),
            goForward: () => navigate(1),
            // v5 block/listen are not supported in v6; return a silent no-op
            // unsubscriber so callers can wire them up without runtime errors.
            block: () => () => {},
            location,
            length: window.history.length,
            action: "PUSH" as const,
            listen: () => () => {},
            createHref: (location: any) => {
                return typeof location === "string"
                    ? location
                    : location.pathname;
            },
        }),
        [navigate, location]
    );
}

export { useLocationV6 as useLocation, useParamsV6 as useParams };
